// Copyright ©️ 2026 eVolve MEP, LLC

import { FLAT_BLACK_FILLER, type HexTriplet, isHexTriplet } from '@evolvemep/api-ts';

import { PT_PER_INCH } from 'components/Autodesk/extensions/MarkupModeExtension/constants';
import EditMode = Autodesk.Viewing.Extensions.Markups.Core.EditMode;

/**
 * Shared config-driven setup for all solid-fill "stamp" markup types (checkmark, cross mark, question mark, etc.).
 *
 * Code references:
 * Markup.js:  https://github.com/wallabyway/forge-markup-measure-extensions/blob/master/Markup/core/Markup.js
 * EditMode.js: https://github.com/wallabyway/forge-markup-measure-extensions/blob/master/Markup/core/edit-modes/EditMode.js
 * EditAction.js: https://github.com/wallabyway/forge-markup-measure-extensions/blob/master/Markup/core/edit-actions/EditAction.js
 */

// Note: the Markups.Core namespace is not available before the markup extension is loaded.
const getMarkupsCore = () => window.Autodesk.Viewing.Extensions.Markups.Core;
const getMarkupUtils = () => getMarkupsCore().Utils;

const DEFAULT_STAMP_COLOR = FLAT_BLACK_FILLER;

export type StampMarkupData = {
  position: { x: number; y: number };
  size: { x: number; y: number };
  rotation: number;
  style: { 'fill-color': HexTriplet; 'fill-opacity': number };
};

const STAMP_TOOL_TYPES = ['checkmark', 'questionMark', 'crossMark'] as const;
export type StampToolType = (typeof STAMP_TOOL_TYPES)[number];
export const isStampToolType = (markupType: string): markupType is StampToolType =>
  STAMP_TOOL_TYPES.some((stampToolType) => markupType === stampToolType);

export type StampConfig = {
  type: EditMode['type'];
  actionPrefix: string;
  getPath: (size: { x: number; y: number }) => (string | number)[];
  getSizeFromPt: (pt: number, editor: Autodesk.Viewing.Extensions.Markups.Core.MarkupsCore) => { x: number; y: number };
};

type PathBounds = { minX: number; minY: number; width: number; height: number };

/**
 * Walks a flat SVG path token array (e.g. `['M', 1, 2, 'L', 3, 4, 'C', …, 'Z']`) and returns the bounding box of the path.
 *
 * Note: If the path contains arc commands, the inferred bounds will be off.
 *
 * This function assumes a string token is followed by coordinate pairs,
 * which handles all path commands except for the arc (A).
 * The arc has a more complex parameter structure,
 * e.g. `A [rx],[ry] [rotation] [large-arc-flag] [sweep-flag] [end-x],[end-y]`.
 */
const inferPathBounds = (tokens: (string | number)[]): PathBounds => {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  let i = 0;
  while (i < tokens.length) {
    const token = tokens[i];
    if (typeof token === 'undefined') {
      i++;
      continue;
    }
    if (typeof token === 'string') {
      i++;
      continue;
    }
    // Consume a coordinate pair.
    const x = token;
    const y = tokens[i + 1];
    if (typeof y === 'number') {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      i += 2;
    } else {
      i++;
    }
  }

  return { minX, minY, width: maxX - minX, height: maxY - minY };
};

/**
 * Creates a StampConfig from a raw path token array.
 *
 * The bounding box is inferred automatically so callers don't need to hardcode aspect ratios or
 * origin offsets. By default, the size input controls height (size.y = pt/72) and width is
 * derived from the natural aspect ratio. Pass a custom `getSizeFromPt` to override this (e.g.
 * the cross mark uses a pixel-aspect correction to appear visually square).
 */
export function makeStampConfig(
  type: EditMode['type'],
  actionPrefix: string,
  pathTokens: (string | number)[],
): StampConfig {
  const bounds = inferPathBounds(pathTokens);
  const { minX, minY, width, height } = bounds;
  const aspectRatio = height / width;

  function getPath(size: { x: number; y: number }): (string | number)[] {
    if (size.x === 1 || size.y === 1) return [...pathTokens]; // No scaling needed.

    const scaleX = size.x / width;
    const scaleY = size.y / height;
    const result: (string | number)[] = [];

    let i = 0;
    while (i < pathTokens.length) {
      const token = pathTokens[i];
      if (typeof token === 'undefined') {
        i++;
        continue;
      }
      if (typeof token === 'string') {
        result.push(token);
        i++;
        continue;
      }
      const x = token;
      const y = pathTokens[i + 1];
      if (typeof y === 'number') {
        // Note: the y-value is negated to account for Autodesk's inverted y-axis.
        result.push((x - minX) * scaleX - size.x / 2, -((y - minY) * scaleY - size.y / 2));
        i += 2;
      } else {
        i++;
      }
    }
    return result;
  }

  const getSizeFromPt: StampConfig['getSizeFromPt'] = (pt) => {
    const heightInches = pt / PT_PER_INCH;
    return { x: heightInches / aspectRatio, y: heightInches };
  };

  return {
    type,
    actionPrefix,
    getPath,
    getSizeFromPt,
  };
}

/**
 * Builds the Markup, EditMode, and EditAction classes for a given StampConfig.
 * Must be called lazily (after MarkupsCore is loaded) because the base classes
 * live under Autodesk.Viewing.Extensions.Markups.Core.
 */
export function createStampClasses(config: StampConfig) {
  const { type, actionPrefix, getPath, getSizeFromPt } = config;
  const markupsCore = getMarkupsCore();
  const markupUtils = getMarkupUtils();

  class MarkupStamp extends markupsCore.Markup {
    /** Assigned to EditModeStamp after it is defined below. */
    static editModeClass: new (
      editor: Autodesk.Viewing.Extensions.Markups.Core.MarkupsCore,
    ) => Autodesk.Viewing.Extensions.Markups.Core.EditMode;

    declare shape: SVGElement & { markup: SVGElement; hitarea: SVGElement };
    declare addMarkupMetadata: (markupNode: SVGElement, metadata: Record<string, string | number>) => Element;

    constructor(id: number, editor: Autodesk.Viewing.Extensions.Markups.Core.MarkupsCore) {
      super(id, editor, ['fill-color', 'fill-opacity']);
      this.type = type;
      this.addMarkupMetadata = markupUtils.addMarkupMetadata.bind(this);
      this.shape = markupUtils.createMarkupPathSvg();
      this.bindDomEvents();

      // Lock the resize and rotation handles.
      this.constraintHeight = true;
      this.constraintWidth = true;
      this.constraintRotation = true;
    }

    getEditMode() {
      return new MarkupStamp.editModeClass(this.editor);
    }

    // Update the markup's SVG shape based on its style and transform properties.
    updateStyle() {
      const { style, shape } = this;
      const path = getPath(this.size).join(' ');

      const fillColor = this.highlighted
        ? this.highlightColor
        : markupUtils.composeRGBAString(String(style['fill-color'] ?? ''), Number(style['fill-opacity'] ?? 1));
      const transform = this.getTransform();

      markupUtils.setAttributeToMarkupSvg(shape, 'd', path);
      markupUtils.setAttributeToMarkupSvg(shape, 'stroke', 'none');
      markupUtils.setAttributeToMarkupSvg(shape, 'stroke-width', 0);
      markupUtils.setAttributeToMarkupSvg(shape, 'fill', fillColor);
      markupUtils.setAttributeToMarkupSvg(shape, 'transform', transform);
      markupUtils.updateMarkupPathSvgHitarea(shape, this.editor);
      shape.setAttribute('pointer-events', 'all');
    }

    // Store the markup's type, transforms, and styles in its SVG shape.
    setMetadata() {
      const style = markupUtils.cloneStyle(this.style);
      const metadata = {
        ...style,
        type: this.type,
        position: [this.position.x, this.position.y].join(' '),
        size: [this.size.x, this.size.y].join(' '),
        rotation: String(this.rotation),
      };
      return this.addMarkupMetadata(this.shape, metadata);
    }
  }

  class StampCreateAction extends markupsCore.EditAction {
    position: { x: number; y: number };
    size: { x: number; y: number };
    rotation: number;
    style: Autodesk.Viewing.Extensions.Markups.Core.Markup['style'];

    constructor(
      editor: Autodesk.Viewing.Extensions.Markups.Core.MarkupsCore,
      id: number,
      position: { x: number; y: number },
      size: { x: number; y: number },
      rotation: number,
      style: Autodesk.Viewing.Extensions.Markups.Core.Markup['style'],
    ) {
      super(editor, `CREATE-${actionPrefix}`, id);
      this.selectOnExecution = false;
      this.position = { x: position.x, y: position.y };
      this.size = { x: size.x, y: size.y };
      this.rotation = rotation;
      this.style = markupUtils.cloneStyle(style);
    }

    redo() {
      const stamp = new MarkupStamp(this.targetId, this.editor);
      this.editor.addMarkup(stamp);
      stamp.setSize(this.position, this.size.x, this.size.y);
      stamp.setRotation(this.rotation);
      stamp.setStyle(this.style);
    }

    undo() {
      const markup = this.editor.getMarkup(this.targetId);
      if (markup) this.editor.removeMarkup(markup);
    }
  }

  class StampResizeAction extends markupsCore.EditAction {
    newSize: { x: number; y: number };
    oldSize: { x: number; y: number };

    constructor(
      editor: Autodesk.Viewing.Extensions.Markups.Core.MarkupsCore,
      stamp: Autodesk.Viewing.Extensions.Markups.Core.Markup,
      ptSize: number,
    ) {
      super(editor, `RESIZE-${actionPrefix}`, Number(stamp.id));
      this.newSize = getSizeFromPt(ptSize, editor);
      this.oldSize = { ...stamp.size };
    }

    redo() {
      const stamp = this.editor.getMarkup(this.targetId);
      if (stamp) {
        stamp.setSize(stamp.position, this.newSize.x, this.newSize.y);
      }
    }

    undo() {
      const stamp = this.editor.getMarkup(this.targetId);
      if (stamp) {
        stamp.setSize(stamp.position, this.oldSize.x, this.oldSize.y);
      }
    }

    isIdentity() {
      return this.newSize.x === this.oldSize.x && this.newSize.y === this.oldSize.y;
    }
  }

  class StampDeleteAction extends markupsCore.EditAction {
    createStamp: StampCreateAction;

    constructor(
      editor: Autodesk.Viewing.Extensions.Markups.Core.MarkupsCore,
      stamp: Autodesk.Viewing.Extensions.Markups.Core.Markup,
    ) {
      super(editor, `DELETE-${actionPrefix}`, Number(stamp.id));
      this.createStamp = new StampCreateAction(
        editor,
        Number(stamp.id),
        stamp.position,
        stamp.size,
        stamp.rotation,
        stamp.getStyle(),
      );
    }

    redo() {
      this.createStamp.undo();
    }

    undo() {
      this.createStamp.redo();
    }
  }

  class EditModeStamp extends markupsCore.EditMode {
    stampSize = 48;

    constructor(editor: Autodesk.Viewing.Extensions.Markups.Core.MarkupsCore) {
      super(editor, type, ['fill-color', 'fill-opacity']);
    }

    /**
     * Parses a saved SVG <g> element back into a StampMarkupData object.
     * Used by MarkupExtension to load editable stamps into the editor on page load.
     */
    getMarkupFromSvg(g: SVGGElement): StampMarkupData | null {
      const metaEl = g.querySelector('metadata');
      if (!metaEl) return null;

      const dataEl = metaEl.querySelector('markup_element');
      if (!dataEl || dataEl.getAttribute('type') !== type) return null;

      const posParts = (dataEl.getAttribute('position') ?? '0 0').split(' ').map(Number);
      const sizeParts = (dataEl.getAttribute('size') ?? '1 1').split(' ').map(Number);
      const [px = 0, py = 0] = posParts;
      const [sx = 1, sy = 1] = sizeParts;
      const rotation = Number(dataEl.getAttribute('rotation') ?? '0');
      const fillColorValue = dataEl.getAttribute('fill-color') ?? '';
      const fillColor = isHexTriplet(fillColorValue) ? fillColorValue : DEFAULT_STAMP_COLOR;
      const fillOpacity = Number(dataEl.getAttribute('fill-opacity') ?? '1');

      return {
        position: { x: px, y: py },
        size: { x: sx, y: sy },
        rotation,
        style: { 'fill-color': fillColor, 'fill-opacity': fillOpacity },
      };
    }

    createMarkup({ position, size, rotation, style }: StampMarkupData, addToHistory = true) {
      const markupId = this.editor.getId();
      const action = new StampCreateAction(this.editor, markupId, position, size, rotation, style);
      action.addToHistory = addToHistory;
      action.execute();
      return true;
    }

    deleteMarkup(markup?: Autodesk.Viewing.Extensions.Markups.Core.Markup, cantUndo?: boolean): boolean {
      const target = markup ?? this.selectedMarkup;
      if (target && target.type === type) {
        const action = new StampDeleteAction(this.editor, target);
        action.addToHistory = !cantUndo;
        action.execute();
        return true;
      }
      return false;
    }

    onMouseDown() {
      super.onMouseDown();
      const { editor } = this;

      // If a markup is selected, don't create a new one.
      if (editor.getSelection()) return;

      const mousePosition = editor.getMousePosition();
      const position = editor.clientToMarkups(mousePosition.x, mousePosition.y);
      const size = getSizeFromPt(this.stampSize, editor);
      const fillColorValue = this.style['fill-color'] ?? '';
      const fillColor = isHexTriplet(fillColorValue) ? fillColorValue : DEFAULT_STAMP_COLOR;

      this.createMarkup({
        position,
        size,
        rotation: 0,
        style: {
          'fill-color': fillColor,
          'fill-opacity': Number(this.style['fill-opacity'] ?? 1),
        },
      });
    }

    resizeSelected(ptSize: number) {
      const { editor } = this;
      const selected = editor.getSelection();
      if (!selected || selected.type !== type) return;
      const action = new StampResizeAction(editor, selected, ptSize);
      action.execute();
    }
  }

  MarkupStamp.editModeClass = EditModeStamp;

  return EditModeStamp;
}
